import { useState } from "react";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sparkle, Plus, Trash } from "@phosphor-icons/react";

export const ISSUE_CATEGORIES = ["general", "plumbing", "electrical", "carpentry", "painting", "roofing", "landscaping", "cleaning", "hvac", "fire_safety", "security", "mould_eradication", "other"];
const TRADE_HINTS = [
  ["painting", /\b(paint|repaint|decorat|stain|plaster)\b/i],
  ["mould_eradication", /\b(mould|mold|damp|condensation)\b/i],
  ["plumbing", /\b(tap|leak|pipe|toilet|wc|cistern|drain|sink|shower|boiler|radiator|water)\b/i],
  ["electrical", /\b(socket|light|lighting|fuse|electric|extractor|fan|wiring|switch|smoke alarm|consumer unit)\b/i],
  ["carpentry", /\b(door|window|frame|skirting|cupboard|hinge|lock|floorboard|worktop)\b/i],
  ["roofing", /\b(roof|gutter|tile|flashing|fascia|soffit)\b/i],
  ["cleaning", /\b(clean|clearance|rubbish|waste)\b/i],
  ["hvac", /\b(heating|ac|air con|ventilation|thermostat)\b/i],
  ["landscaping", /\b(garden|hedge|grass|fence|patio|tree)\b/i],
  ["security", /\b(alarm|cctv|intercom|gate|security)\b/i],
  ["fire_safety", /\b(fire|extinguisher|emergency light|fire door)\b/i],
];

const guessCategory = (t) => (TRADE_HINTS.find(([, re]) => re.test(t)) || ["general"])[0];
let seq = 0;
const row = (title, category, checked = true, instructions = "") => ({ key: `i${++seq}`, title, category: category || guessCategory(title), checked, instructions });

/* Heuristic pre-fill: one row per line / bullet / numbered item / semicolon-separated clause of the request text */
export function issuesFromText(text) {
  const parts = String(text || "").split(/\r?\n|;|(?<=\.)\s+(?=[A-Z0-9])/).map((s) => s.replace(/^\s*(?:[-•*]|\d+[.)])\s*/, "").trim()).filter((s) => s.length > 3);
  return parts.slice(0, 15).map((p) => row(p.replace(/\.$/, "").slice(0, 120)));
}

export const selectedIssues = (rows) => rows.filter((r) => r.checked && r.title.trim()).map(({ title, category, instructions }) => ({ title: title.trim(), category, instructions }));

/* Tick / edit the issues that become sub-jobs when a request is split */
export default function SplitIssuesEditor({ rows, onChange, requestId, requestText }) {
  const [aiBusy, setAiBusy] = useState(false);
  const update = (key, patch) => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const remove = (key) => onChange(rows.filter((r) => r.key !== key));
  const add = () => onChange([...rows, row("", "general")]);
  const suggest = async () => {
    setAiBusy(true);
    try {
      const { data } = await API.post(`/job-requests/${requestId}/suggest-issues`, {});
      onChange((data.issues || []).map((i) => row(i.title, i.category, true, i.instructions || "")));
      toast.success(`AI found ${data.issues.length} issue${data.issues.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI suggestion failed"); }
    finally { setAiBusy(false); }
  };
  const picked = rows.filter((r) => r.checked && r.title.trim()).length;

  return (
    <div className="space-y-2" data-testid="split-issues-editor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground" data-testid="split-issues-count">{picked}</span> sub-job{picked === 1 ? "" : "s"} will be created — tick the issues, fix the wording, pick the trade.</p>
        <div className="flex gap-1.5">
          <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={suggest} disabled={aiBusy || !requestText?.trim()} data-testid="split-ai-suggest-btn">
            <Sparkle size={12} className="mr-1" /> {aiBusy ? "Reading request…" : "Suggest with AI"}
          </Button>
          <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={add} data-testid="split-add-issue-btn"><Plus size={12} className="mr-1" /> Add issue</Button>
        </div>
      </div>
      <div className="border border-border rounded-md divide-y divide-border max-h-72 overflow-y-auto">
        {rows.length === 0 && <p className="p-3 text-xs text-muted-foreground">No issues yet — add them by hand or let the AI read the request.</p>}
        {rows.map((r, i) => (
          <div key={r.key} className={`flex items-center gap-2 px-2 py-1.5 ${r.checked ? "" : "opacity-60"}`} data-testid={`split-issue-row-${i}`}>
            <input type="checkbox" className="h-4 w-4 rounded shrink-0" checked={r.checked} onChange={(e) => update(r.key, { checked: e.target.checked })} data-testid={`split-issue-check-${i}`} />
            <Input value={r.title} onChange={(e) => update(r.key, { title: e.target.value })} placeholder="Describe the issue…" className="h-7 text-xs flex-1" data-testid={`split-issue-title-${i}`} />
            <Select value={r.category} onValueChange={(v) => update(r.key, { category: v })}>
              <SelectTrigger className="h-7 w-32 text-[11px] shrink-0" data-testid={`split-issue-category-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>{ISSUE_CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize text-xs">{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
            <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive shrink-0" onClick={() => remove(r.key)} data-testid={`split-issue-remove-${i}`}><Trash size={12} /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
